import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

const SERVICE_MAP = {
  antstalk: { name: 'AntsTalk', color: '#facc15', icon: '💬' },
  antsmusic: { name: 'AntsMusic', color: '#8b5cf6', icon: '🎵' },
  // 스카이스크라이브 이름으로 쌓인 기록이 3천 건 넘게 남아 있다.
  // 키를 지우면 그 기록이 이름 없는 항목이 되므로 함께 유지한다.
  skyscribe: { name: 'SkyScribe', color: '#8b5cf6', icon: '🎵' },
  nodemaster: { name: 'NodeMaster', color: '#3b82f6', icon: '📁' },
  edu: { name: 'Edu', color: '#10b981', icon: '📚' },
  store: { name: 'Store', color: '#f97316', icon: '🛒' },
  gantt: { name: 'GanttChart', color: '#06b6d4', icon: '📊' },
  brand: { name: 'Brand', color: '#ec4899', icon: '🏷️' },
  story: { name: 'Story', color: '#a78bfa', icon: '📖' },
  estimate: { name: 'Estimate', color: '#f43f5e', icon: '🧾' },
};

const ACTION_LABELS = {
  LOGIN: '로그인',
  LOGOUT: '로그아웃',
  REGISTER: '회원가입',
  PROFILE_UPDATE: '프로필 수정',
  PASSWORD_CHANGE: '비밀번호 변경',
  FRIEND_REQUEST: '친구 요청',
  FRIEND_ACCEPT: '친구 수락',
  CHAT_CREATE: '채팅방 생성',
  MESSAGE_SEND: '메시지 전송',
  POST_CREATE: '게시글 작성',
  POST_DELETE: '게시글 삭제',
  FILE_CREATE: '파일 생성',
  FILE_DELETE: '파일 삭제',
  SONG_LIKE: '곡 좋아요',
  ALBUM_CREATE: '앨범 생성',
};

function formatDate(s) {
  if (!s) return '-';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function relativeTime(s) {
  if (!s) return '';
  const d = new Date(s);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return '방금 전';
  if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}일 전`;
  return formatDate(s);
}

export default function ActivityLog() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userIdFilter = searchParams.get('userId') || 'all';
  const [filteredUser, setFilteredUser] = useState(null);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [serviceFilter, setServiceFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [alertsOpen, setAlertsOpen] = useState(false);

  const loadAlerts = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/activity-alerts', { credentials: 'include' });
      const data = await res.json();
      if (data.success) setAlerts(data.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const toggleAlert = async (serviceCode, enabled) => {
    // 낙관적 반영 후 실패하면 되돌림
    setAlerts((prev) => prev.map((a) => (a.service_code === serviceCode ? { ...a, enabled } : a)));
    try {
      const res = await fetch(`/api/admin/activity-alerts/${serviceCode}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
    } catch (e) {
      console.error(e);
      setAlerts((prev) => prev.map((a) => (a.service_code === serviceCode ? { ...a, enabled: !enabled } : a)));
      alert('알림 설정 변경에 실패했습니다.');
    }
  };

  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: 30,
        service: serviceFilter,
        search,
        userId: userIdFilter,
      });
      const res = await fetch(`/api/admin/activity-log?${params}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setItems(data.data);
        setStats(data.stats || {});
        setPagination((p) => ({ ...p, totalPages: data.pagination.totalPages, total: data.pagination.total }));
        if (userIdFilter !== 'all' && data.data.length > 0 && !filteredUser) {
          const r = data.data[0];
          setFilteredUser({ name: r.nickname || r.user_name || r.user_account, email: r.user_email, avatar: r.avatar_url });
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, serviceFilter, search, userIdFilter, filteredUser]);

  useEffect(() => { load(); }, [load]);

  const clearUserFilter = () => {
    setFilteredUser(null);
    navigate('/Admin/activity');
  };

  return (
    <>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">활동 내역</h1>
          <p className="admin-page-desc">{filteredUser ? `${filteredUser.name} 의 활동만 표시 중` : '전체 사용자의 SSO 활동 로그를 확인합니다.'}</p>
        </div>
      </div>

      {filteredUser && (
        <div className="admin-card" style={{ padding: 12, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          {filteredUser.avatar ? (
            <img src={filteredUser.avatar} alt="" style={{ width: 36, height: 36, borderRadius: '50%' }} />
          ) : (
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          )}
          <div style={{ flex: 1 }}>
            <div style={{ color: '#fff', fontWeight: 600 }}>{filteredUser.name}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{filteredUser.email}</div>
          </div>
          <button className="admin-filter-pill" onClick={clearUserFilter}>전체 보기 ×</button>
        </div>
      )}

      {/* 앤츠톡 푸시 알림 설정 (서비스별 on/off) */}
      <div className="admin-card" style={{ padding: 16, marginBottom: 16 }}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
          onClick={() => setAlertsOpen((v) => !v)}
        >
          <span style={{ fontSize: 16 }}>🔔</span>
          <div style={{ flex: 1 }}>
            <div style={{ color: '#fff', fontWeight: 600 }}>앤츠톡 실시간 알림</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>
              {alerts.length === 0
                ? '설정 불러오는 중…'
                : `${alerts.filter((a) => a.enabled).length}/${alerts.length}개 서비스 켜짐`}
            </div>
          </div>
          <span style={{ color: 'rgba(255,255,255,0.5)' }}>{alertsOpen ? '▲' : '▼'}</span>
        </div>

        {alertsOpen && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
            {alerts.map((a) => {
              const meta = SERVICE_MAP[a.service_code];
              return (
                <button
                  key={a.service_code}
                  onClick={() => toggleAlert(a.service_code, !a.enabled)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 14px', borderRadius: 20, cursor: 'pointer',
                    border: `1px solid ${a.enabled ? (meta?.color || '#01BCD6') : 'rgba(255,255,255,0.15)'}`,
                    background: a.enabled ? `${meta?.color || '#01BCD6'}22` : 'transparent',
                    color: a.enabled ? '#fff' : 'rgba(255,255,255,0.45)',
                    fontSize: 13,
                  }}
                >
                  <span>{meta?.icon || '❓'}</span>
                  <span>{meta?.name || a.service_code}</span>
                  <span style={{ fontSize: 11, opacity: 0.8 }}>{a.enabled ? 'ON' : 'OFF'}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* 통계 */}
      <div className="stats-grid">
        <StatCard label="전체 활동" value={Number(stats.total || 0).toLocaleString()} accent="#01BCD6" />
        <StatCard label="고유 사용자" value={Number(stats.unique_users || 0).toLocaleString()} accent="#34d399" />
        <StatCard label="최근 24시간" value={Number(stats.last_24h || 0).toLocaleString()} accent="#f59e0b" />
      </div>

      {/* 필터 + 검색 */}
      <div className="admin-card" style={{ padding: 16, marginBottom: 16 }}>
        <div className="admin-filter-bar" style={{ marginBottom: 12 }}>
          <button
            className={'admin-filter-pill' + (serviceFilter === 'all' ? ' active' : '')}
            onClick={() => { setServiceFilter('all'); setPagination((p) => ({ ...p, page: 1 })); }}
          >전체</button>
          {Object.entries(SERVICE_MAP).map(([key, svc]) => (
            <button
              key={key}
              className={'admin-filter-pill' + (serviceFilter === key ? ' active' : '')}
              onClick={() => { setServiceFilter(key); setPagination((p) => ({ ...p, page: 1 })); }}
            >{svc.icon} {svc.name}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            placeholder="사용자 검색 (이름/이메일/계정)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setSearch(searchInput); setPagination((p) => ({ ...p, page: 1 })); } }}
            className="admin-input"
            style={{ flex: 1 }}
          />
          <button
            className="admin-filter-pill active"
            onClick={() => { setSearch(searchInput); setPagination((p) => ({ ...p, page: 1 })); }}
          >검색</button>
        </div>
      </div>

      {/* 테이블 */}
      <div className="admin-card" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>시간</th>
                <th>사용자</th>
                <th>서비스</th>
                <th>활동</th>
                <th>상세</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.4)' }}>불러오는 중...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.4)' }}>활동 내역이 없습니다.</td></tr>
              ) : items.map((item) => {
                const svc = SERVICE_MAP[item.service_code] || { name: item.service_code, color: '#6b7280', icon: '🔹' };
                return (
                  <tr key={item.id}>
                    <td title={formatDate(item.created_at)} style={{ whiteSpace: 'nowrap' }}>{relativeTime(item.created_at)}</td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: 500, color: '#fff' }}>{item.nickname || item.user_name || item.user_account || '-'}</span>
                        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{item.user_email || item.user_account || ''}</span>
                      </div>
                    </td>
                    <td>
                      <span style={{
                        display: 'inline-block',
                        padding: '3px 10px',
                        borderRadius: 999,
                        background: svc.color + '22',
                        color: svc.color,
                        fontSize: 11,
                        fontWeight: 600,
                        border: `1px solid ${svc.color}44`,
                      }}>
                        {svc.icon} {svc.name}
                      </span>
                    </td>
                    <td style={{ color: 'rgba(255,255,255,0.85)' }}>{ACTION_LABELS[item.action] || item.action}</td>
                    <td style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.detail ? (typeof item.detail === 'string' ? item.detail : JSON.stringify(item.detail)) : ''}
                    </td>
                    <td style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace' }}>{item.ip_address || ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* 페이지네이션 */}
      {pagination.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20, alignItems: 'center' }}>
          <button
            className="admin-filter-pill"
            disabled={pagination.page <= 1}
            onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
          >이전</button>
          <span style={{ padding: '6px 12px', fontSize: 13, color: 'rgba(255,255,255,0.65)' }}>{pagination.page} / {pagination.totalPages}</span>
          <button
            className="admin-filter-pill"
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
          >다음</button>
        </div>
      )}
    </>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="admin-card" style={{ padding: '20px 24px', borderLeft: `3px solid ${accent}` }}>
      <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.5)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: '#fff' }}>{value}</div>
    </div>
  );
}
